import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useApiClient } from './apiContext';
import type { RequestOptions } from './client';

/**
 * Thin wrappers that pair TanStack Query with the application's one API client.
 *
 * Two things every screen would otherwise repeat: reaching for the client through context, and
 * remembering which cached queries a write invalidates. Getting the second wrong is what produces
 * a staff list that still shows someone a minute after they were removed — a bug that looks like a
 * backend fault and is not one.
 */

export function useApiQuery<T>(
  key: readonly unknown[],
  path: string,
  options: {
    readonly enabled?: boolean;
    /**
     * Keep showing the previous answer while a new key loads. For charts whose filters change
     * under them (CR-33): the picture updates in place instead of collapsing to a skeleton and
     * back on every click.
     */
    readonly keepPrevious?: boolean;
  } = {},
): UseQueryResult<T> {
  const client = useApiClient();
  return useQuery<T>({
    queryKey: key,
    queryFn: ({ signal }) => client.get<T>(path, { signal }),
    enabled: options.enabled ?? true,
    placeholderData: options.keepPrevious ? keepPreviousData : undefined,
  });
}

export interface ApiMutationOptions<TVariables> {
  /** Query key prefixes to refetch once the write succeeds. */
  readonly invalidates?: readonly (readonly unknown[])[];
  readonly onSuccess?: (variables: TVariables) => void;
}

export function useApiMutation<TResult, TVariables>(
  request: (variables: TVariables) => { readonly path: string; readonly options: RequestOptions },
  { invalidates = [], onSuccess }: ApiMutationOptions<TVariables> = {},
): UseMutationResult<TResult, unknown, TVariables> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<TResult, unknown, TVariables>({
    mutationFn: (variables) => {
      const { path, options } = request(variables);
      return client.request<TResult>(path, options);
    },
    onSuccess: (_result, variables) => {
      invalidates.forEach((key) => {
        void queryClient.invalidateQueries({ queryKey: key });
      });
      onSuccess?.(variables);
    },
  });
}

/** Query keys in one place, so an invalidation and its query cannot drift apart. */
export const queryKeys = {
  branches: (clinicId: string) => ['clinics', clinicId, 'branches'] as const,
  rooms: (clinicId: string, branchId: string) => ['clinics', clinicId, 'branches', branchId, 'rooms'] as const,
  workingHours: (clinicId: string, branchId: string) =>
    ['clinics', clinicId, 'branches', branchId, 'working-hours'] as const,
  holidays: (clinicId: string, branchId: string) =>
    ['clinics', clinicId, 'branches', branchId, 'holidays'] as const,
  services: (clinicId: string) => ['clinics', clinicId, 'catalog', 'services'] as const,
  serviceAreas: (clinicId: string, serviceId: string) =>
    ['clinics', clinicId, 'catalog', 'services', serviceId, 'areas'] as const,
  seedPack: (clinicId: string) => ['clinics', clinicId, 'catalog', 'seed-pack'] as const,
  offerings: (clinicId: string, branchId: string) =>
    ['clinics', clinicId, 'catalog', 'offerings', branchId] as const,
  packages: (clinicId: string) => ['clinics', clinicId, 'packages'] as const,
  // Under the packages prefix, so changing a package refreshes the charts (CR-36).
  packageSummary: (clinicId: string, search: string) => ['clinics', clinicId, 'packages', 'summary', search] as const,
  promotions: (clinicId: string) => ['clinics', clinicId, 'promotions'] as const,
  policies: (clinicId: string) => ['clinics', clinicId, 'settings', 'policies'] as const,
  staff: (clinicId: string) => ['clinics', clinicId, 'staff'] as const,
  settings: (clinicId: string) => ['clinics', clinicId, 'settings'] as const,
  supportGrants: (clinicId: string) => ['clinics', clinicId, 'support-grants'] as const,
  audit: (clinicId: string, query: string) => ['clinics', clinicId, 'audit', query] as const,
  platformClinics: () => ['platform', 'clinics'] as const,
  platformClinic: (clinicId: string) => ['platform', 'clinics', clinicId] as const,
  platformInvoices: () => ['platform', 'invoices'] as const,
  platformClinicInvoices: (clinicId: string) => ['platform', 'clinics', clinicId, 'invoices'] as const,
  platformFeatureFlags: (clinicId: string) => ['platform', 'clinics', clinicId, 'feature-flags'] as const,
  platformAudit: (query: string) => ['platform', 'audit', query] as const,
  platformSupportGrants: () => ['platform', 'support', 'grants'] as const,

  // ── Milestone 4: money ──────────────────────────────────────────────────
  sales: (clinicId: string, query: string) => ['clinics', clinicId, 'sales', query] as const,
  // Five parts, so it never equals a day's list (four); under the sales prefix, so recording a
  // sale refreshes the charts with the list (CR-34).
  salesSummary: (clinicId: string, search: string) => ['clinics', clinicId, 'sales', 'summary', search] as const,
  sale: (clinicId: string, saleId: string) => ['clinics', clinicId, 'sales', saleId] as const,
  patientSales: (clinicId: string, membershipId: string) =>
    ['clinics', clinicId, 'patients', membershipId, 'sales'] as const,
  wallet: (clinicId: string, membershipId: string) =>
    ['clinics', clinicId, 'patients', membershipId, 'wallet'] as const,
  walletHistory: (clinicId: string, membershipId: string) =>
    ['clinics', clinicId, 'patients', membershipId, 'wallet', 'history'] as const,
  expiringGrants: (clinicId: string) => ['clinics', clinicId, 'wallet', 'expiring'] as const,
  refunds: (clinicId: string, query: string) => ['clinics', clinicId, 'refunds', query] as const,
  expenses: (clinicId: string, query: string) => ['clinics', clinicId, 'expenses', query] as const,
  expenseTotals: (clinicId: string, query: string) =>
    ['clinics', clinicId, 'expenses', 'totals', query] as const,
  cashSummary: (clinicId: string, branchId: string, day: string) =>
    ['clinics', clinicId, 'cash-summary', branchId, day] as const,
  moneyPolicies: (clinicId: string) => ['clinics', clinicId, 'settings', 'money'] as const,

  // ── Milestone 3 ─────────────────────────────────────────────────────────
  patients: (clinicId: string, query: string) => ['clinics', clinicId, 'patients', query] as const,
  patient: (clinicId: string, membershipId: string) =>
    ['clinics', clinicId, 'patients', membershipId] as const,
  patientAppointments: (clinicId: string, membershipId: string) =>
    ['clinics', clinicId, 'patients', membershipId, 'appointments'] as const,
  patientsByPhone: (clinicId: string, phone: string) =>
    ['clinics', clinicId, 'patients', 'by-phone', phone] as const,
  // Under the patients prefix on purpose: creating a patient invalidates that prefix, so the
  // overview's counts refresh with the list. Five parts, so it can never equal a search for the
  // word "overview" (four parts) or a patient's own key (a UUID in the fourth place).
  patientOverview: (clinicId: string) => ['clinics', clinicId, 'patients', 'overview', 'counts'] as const,
  appointmentSearch: (clinicId: string, search: string) =>
    ['clinics', clinicId, 'appointments', 'search', search] as const,
  appointmentSummary: (clinicId: string, search: string) =>
    ['clinics', clinicId, 'appointments', 'summary', search] as const,
  // The board's key carries the branch and the date only. The schedule version is deliberately
  // absent: a change bumps the version, and this key is invalidated by the socket rather than by
  // being different — putting the version in the key would leave the previous day's data cached
  // under a key nothing ever invalidates.
  dayBoard: (clinicId: string, branchId: string, on: string) =>
    ['clinics', clinicId, 'appointments', 'board', branchId, on] as const,
  appointment: (clinicId: string, appointmentId: string) =>
    ['clinics', clinicId, 'appointments', appointmentId] as const,
  appointmentHistory: (clinicId: string, appointmentId: string) =>
    ['clinics', clinicId, 'appointments', appointmentId, 'history'] as const,
  availability: (clinicId: string, query: string) =>
    ['clinics', clinicId, 'availability', query] as const,
  workingDoctors: (clinicId: string, branchId: string, on: string) =>
    ['clinics', clinicId, 'doctors', 'working', branchId, on] as const,
  doctorAvailability: (clinicId: string, employmentId: string) =>
    ['clinics', clinicId, 'doctors', employmentId, 'availability'] as const,
  doctorOverrides: (clinicId: string, employmentId: string) =>
    ['clinics', clinicId, 'doctors', employmentId, 'overrides'] as const,
  worklists: (clinicId: string, branchId: string) =>
    ['clinics', clinicId, 'worklists', branchId] as const,
  worklist: (clinicId: string, worklistId: string) =>
    ['clinics', clinicId, 'worklists', worklistId] as const,

  // ── Milestone 5: branding, templates and the waiting list ───────────────
  branding: (clinicId: string) => ['clinics', clinicId, 'branding'] as const,
  // The locale is part of the key: Arabic and English are separate rows, and a screen that
  // switched language without refetching would show one and save the other.
  notificationTemplates: (clinicId: string, locale: string) =>
    ['clinics', clinicId, 'notifications', 'templates', locale] as const,
  notificationRoutes: (clinicId: string) => ['clinics', clinicId, 'notifications', 'routes'] as const,
  notificationDeliveries: (clinicId: string, query: string) =>
    ['clinics', clinicId, 'notifications', 'deliveries', query] as const,
  waitlist: (clinicId: string, branchId: string) => ['clinics', clinicId, 'waitlist', branchId] as const,

  // ── Milestone 6: reassignment ───────────────────────────────────────────
  // Both keyed under the appointment rather than the worklist: a proposal is about an appointment,
  // and the same one may be looked at from the day-board as well as from the queue.
  reassignmentSuggestions: (clinicId: string, appointmentId: string) =>
    ['clinics', clinicId, 'reassignments', appointmentId, 'suggestions'] as const,
  proposals: (clinicId: string, appointmentId: string) =>
    ['clinics', clinicId, 'reassignments', appointmentId] as const,

  // ── Milestone 7: the medical layer ──────────────────────────────────────
  // Everything clinical hangs off the patient, because that is the only way any of it is ever
  // looked at — and because invalidating "this patient's clinical data" after a signature or an
  // intake is one prefix rather than five keys somebody has to remember.
  clinicalContext: (clinicId: string, membershipId: string, serviceId: string) =>
    ['clinics', clinicId, 'clinical', membershipId, serviceId] as const,
  intake: (clinicId: string, membershipId: string) =>
    ['clinics', clinicId, 'clinical', membershipId, 'intake'] as const,
  intakeHistory: (clinicId: string, membershipId: string) =>
    ['clinics', clinicId, 'clinical', membershipId, 'intake', 'history'] as const,
  patchTests: (clinicId: string, membershipId: string) =>
    ['clinics', clinicId, 'clinical', membershipId, 'patch-tests'] as const,
  consentSignatures: (clinicId: string, membershipId: string) =>
    ['clinics', clinicId, 'clinical', membershipId, 'consents'] as const,
  outstandingConsents: (clinicId: string, membershipId: string, serviceId: string) =>
    ['clinics', clinicId, 'clinical', membershipId, 'consents', 'outstanding', serviceId] as const,
  photoSets: (clinicId: string, membershipId: string) =>
    ['clinics', clinicId, 'clinical', membershipId, 'photo-sets'] as const,
  outstandingSafetyRecords: (clinicId: string) =>
    ['clinics', clinicId, 'clinical', 'safety-records', 'outstanding'] as const,
  consentTemplates: (clinicId: string) => ['clinics', clinicId, 'clinical', 'consent-templates'] as const,
  aftercareTemplates: (clinicId: string) => ['clinics', clinicId, 'clinical', 'aftercare'] as const,
  clinicalPolicy: (clinicId: string) => ['clinics', clinicId, 'clinical', 'policy'] as const,

  // ── Milestone 8: the shelf and the fleet ────────────────────────────────
  // The item catalogue is clinic-wide and everything else is per branch, which is exactly how the
  // keys are shaped — so receiving stock at Zamalek does not refetch New Cairo's shelf.
  stockItems: (clinicId: string) => ['clinics', clinicId, 'inventory', 'items'] as const,
  stockBalances: (clinicId: string, branchId: string) =>
    ['clinics', clinicId, 'inventory', 'stock', branchId] as const,
  stockCard: (clinicId: string, branchId: string, itemId: string) =>
    ['clinics', clinicId, 'inventory', 'stock', branchId, itemId] as const,
  usageRequests: (clinicId: string, query: string) =>
    ['clinics', clinicId, 'inventory', 'usage-requests', query] as const,
  stockTransfers: (clinicId: string, query: string) =>
    ['clinics', clinicId, 'inventory', 'transfers', query] as const,
  stockAlerts: (clinicId: string, branchId: string) =>
    ['clinics', clinicId, 'inventory', 'alerts', branchId] as const,
  inventorySettings: (clinicId: string) => ['clinics', clinicId, 'inventory', 'settings'] as const,

  machines: (clinicId: string, branchId: string) => ['clinics', clinicId, 'machines', branchId] as const,
  machine: (clinicId: string, machineId: string) => ['clinics', clinicId, 'machines', machineId] as const,
  machineDowntime: (clinicId: string, machineId: string) =>
    ['clinics', clinicId, 'machines', machineId, 'downtime'] as const,
  machineDocuments: (clinicId: string, machineId: string) =>
    ['clinics', clinicId, 'machines', machineId, 'documents'] as const,
  maintenanceJobs: (clinicId: string, query: string) =>
    ['clinics', clinicId, 'machines', 'maintenance', query] as const,
  machineCounters: (clinicId: string, query: string) =>
    ['clinics', clinicId, 'machines', 'counters', query] as const,
  machineAlerts: (clinicId: string, branchId: string) =>
    ['clinics', clinicId, 'machines', 'alerts', branchId] as const,
  machineSettings: (clinicId: string) => ['clinics', clinicId, 'machines', 'settings'] as const,

  // ── Milestone 9: reports and statements ─────────────────────────────────
  reportCatalogue: (clinicId: string) => ['clinics', clinicId, 'reports'] as const,
  // The whole filter is in the key. A report is a pure function of its parameters, so two different
  // periods are two different answers and caching them under one key would show March's figures
  // for a moment after somebody asked for April's.
  report: (clinicId: string, query: string) => ['clinics', clinicId, 'reports', query] as const,
  statements: (clinicId: string, period: string) => ['clinics', clinicId, 'statements', period] as const,
  statement: (clinicId: string, employmentId: string, period: string) =>
    ['clinics', clinicId, 'statements', employmentId, period] as const,
  compensation: (clinicId: string, employmentId: string) =>
    ['clinics', clinicId, 'statements', employmentId, 'compensation'] as const,

  // ── Milestone 9: chat, broadcasts, the bell ─────────────────────────────
  // The inbox key carries the branch and the status filter. A closed-thread view and a live one
  // are two different answers, and caching them together is how a receptionist sees a conversation
  // she just closed reappear.
  chatInbox: (clinicId: string, branchId: string, status: string) =>
    ['clinics', clinicId, 'chat', 'inbox', branchId, status] as const,
  chatThread: (clinicId: string, threadId: string) =>
    ['clinics', clinicId, 'chat', 'threads', threadId] as const,
  chatMessages: (clinicId: string, threadId: string) =>
    ['clinics', clinicId, 'chat', 'threads', threadId, 'messages'] as const,
  broadcasts: (clinicId: string) => ['clinics', clinicId, 'broadcasts'] as const,
  broadcastEstimate: (clinicId: string, query: string) =>
    ['clinics', clinicId, 'broadcasts', 'estimate', query] as const,
  broadcastRecipients: (clinicId: string, broadcastId: string) =>
    ['clinics', clinicId, 'broadcasts', broadcastId, 'recipients'] as const,
  staffNotifications: () => ['me', 'notifications'] as const,
  staffNotificationCount: () => ['me', 'notifications', 'unread'] as const,
  notificationSettings: (clinicId: string) =>
    ['clinics', clinicId, 'notifications', 'settings'] as const,
} as const;
